import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';
import ScrollToTopButton from '../../components/feature/ScrollToTopButton';
import { addSchemaToPage, removePageSchemas, getBreadcrumbSchema, getWebPageSchema, getServiceSchema } from '../../utils/seo';

const ServiciosPage = () => {
  const [heroRef, heroInView] = useInView({ triggerOnce: true, threshold: 0.1 });

  useEffect(() => {
    document.title = 'Servicios de Pladur en Badalona | Tabiques, Techos y Aislamiento';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Servicios de instalación de pladur en Badalona: tabiques, trasdosados, falsos techos, aislamiento acústico y térmico. Presupuesto sin compromiso.');
    }

    // Add Schema.org JSON-LD
    const schemas = [
      addSchemaToPage(getBreadcrumbSchema([
        { name: 'Inicio', url: '/' },
        { name: 'Servicios', url: '/servicios' }
      ])),
      addSchemaToPage(getWebPageSchema(
        'Servicios de Pladur - PLADUREX',
        'Servicios de instalación de pladur en Badalona: tabiques, trasdosados, falsos techos, aislamiento acústico y térmico.',
        '/servicios'
      )),
      addSchemaToPage(getServiceSchema())
    ];

    return () => {
      removePageSchemas();
    };
  }, []);

  const services = [
    {
      id: 'tabiques',
      icon: 'ri-layout-line',
      title: 'Tabiques de pladur',
      description: 'Divisiones interiores rápidas y limpias para redistribuir espacios',
      includes: [
        'Tabiques simples y dobles',
        'Con o sin aislamiento acústico',
        'Paso de instalaciones (electricidad, fontanería)',
        'Acabado listo para pintar',
        'Refuerzos para colgar elementos pesados',
      ],
      useCases: 'Ideal para crear nuevas habitaciones, separar ambientes en lofts, dividir oficinas o locales comerciales.',
      priceFactors: 'El precio depende de los m² de tabique, altura del techo, tipo de aislamiento, número de puertas y nivel de acabado.',
    },
    {
      id: 'trasdosados',
      icon: 'ri-building-line',
      title: 'Trasdosados',
      description: 'Revestimiento de paredes para mejorar aislamiento y ocultar instalaciones',
      includes: [
        'Trasdosado directo (pegado a pared)',
        'Trasdosado autoportante (con estructura)',
        'Aislamiento térmico y acústico',
        'Placas antihumedad si es necesario',
        'Acabado liso para pintar',
      ],
      useCases: 'Perfecto para mejorar el aislamiento de fachadas, ocultar tuberías, corregir paredes irregulares o ganar espacio para instalaciones.',
      priceFactors: 'Varía según m² de superficie, tipo de trasdosado (directo o autoportante), grosor de aislamiento y complejidad de la pared.',
    },
    {
      id: 'techos',
      icon: 'ri-stack-line',
      title: 'Falsos techos',
      description: 'Techos continuos y registrables para mejorar estética y funcionalidad',
      includes: [
        'Techos continuos lisos',
        'Techos registrables desmontables',
        'Integración de iluminación LED',
        'Aislamiento acústico superior',
        'Rebajes y diseños personalizados',
      ],
      useCases: 'Ideal para ocultar instalaciones, mejorar la acústica, reducir altura de techos altos, integrar iluminación o climatización.',
      priceFactors: 'El coste depende de m² de techo, altura de la estancia, tipo de techo (continuo o registrable), iluminación integrada y accesos.',
    },
    {
      id: 'aislamiento-acustico',
      icon: 'ri-volume-mute-line',
      title: 'Aislamiento acústico',
      description: 'Soluciones de insonorización para reducir ruidos molestos',
      includes: [
        'Lana de roca de alta densidad',
        'Placas de pladur acústicas',
        'Sistemas de doble placa',
        'Bandas resilientes antivibratorias',
        'Sellado acústico de juntas',
      ],
      useCases: 'Esencial para dormitorios, salas de música, oficinas con reuniones, locales comerciales o viviendas con vecinos ruidosos.',
      priceFactors: 'Depende del nivel de aislamiento requerido, grosor del sistema, superficie a tratar y complejidad de la instalación.',
    },
    {
      id: 'aislamiento-termico',
      icon: 'ri-temp-cold-line',
      title: 'Aislamiento térmico',
      description: 'Mejora la eficiencia energética y el confort de tu hogar',
      includes: [
        'Lana mineral o poliestireno',
        'Trasdosados con aislamiento',
        'Reducción de puentes térmicos',
        'Mejora del certificado energético',
        'Ahorro en calefacción y aire acondicionado',
      ],
      useCases: 'Recomendado para fachadas frías, viviendas con problemas de condensación, locales con climatización o reformas energéticas.',
      priceFactors: 'El precio varía según tipo de aislante, grosor necesario, m² de superficie y complejidad de la instalación.',
    },
    {
      id: 'antihumedad',
      icon: 'ri-drop-line',
      title: 'Pladur antihumedad',
      description: 'Placas especiales resistentes a la humedad para zonas húmedas',
      includes: [
        'Placas hidrófugas (color verde)',
        'Resistencia a ambientes húmedos',
        'Ideal para baños y cocinas',
        'Prevención de moho y hongos',
        'Compatible con alicatado',
      ],
      useCases: 'Imprescindible en baños, cocinas, lavaderos, sótanos o cualquier zona con alta humedad ambiental.',
      priceFactors: 'Similar al pladur estándar con un pequeño incremento por el tipo de placa. Depende de m² y complejidad.',
    },
  ];

  return (
    <div className="min-h-screen bg-dark-bg">
      <Header />
      
      <main>
        {/* Hero Section */}
        <section ref={heroRef} className="relative pt-36 md:pt-32 pb-20 px-6 overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img
              src="https://public.readdy.ai/ai/img_res/d0f5566d8128542c956756ccf2e65a38.jpg"
              alt="Servicios de pladur"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/60 to-dark-bg"></div>
          </div>

          <div className="relative z-10 max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={heroInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6 }}
              className="inline-block px-4 py-2 bg-orange-primary/10 border border-orange-primary rounded-full mb-6"
            >
              <span className="text-orange-primary text-sm font-bold">Nuestros servicios</span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={heroInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-text-primary mb-6"
            >
              Servicios de pladur en Badalona
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={heroInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="text-lg md:text-xl text-text-secondary max-w-3xl mx-auto leading-relaxed"
            >
              Soluciones completas para tu hogar, oficina o local comercial. <Link to="/empresa" className="text-orange-primary hover:underline">Conoce nuestra empresa</Link>
            </motion.p>
          </div>
        </section>

        {/* Services Detail Sections */}
        <div className="max-w-7xl mx-auto px-6">
          {services.map((service, index) => (
            <ServiceDetailSection 
              key={service.id}
              service={service}
              index={index}
            />
          ))}
        </div>

        {/* FAQ Link Section */}
        <section className="py-16 px-6 bg-dark-card">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl font-extrabold text-text-primary mb-4">
                ¿Tienes dudas sobre nuestros servicios?
              </h2>
              <p className="text-text-secondary text-lg mb-6">
                Consulta las preguntas más frecuentes sobre instalación de pladur
              </p>
              <Link
                to="/faq"
                className="inline-flex items-center space-x-2 text-orange-primary hover:underline font-medium text-lg"
              >
                <span>Ver preguntas frecuentes</span>
                <i className="ri-arrow-right-line"></i>
              </Link>
            </motion.div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="py-24 px-6 bg-gradient-to-r from-orange-dark to-orange-primary">
          <div className="max-w-4xl mx-auto text-center">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-4xl md:text-5xl font-extrabold text-white mb-6"
            >
              ¿Necesitas más información?
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-xl text-white/90 mb-10"
            >
              Llámanos o envíanos un WhatsApp y te asesoramos sin compromiso
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <Link
                to="/contacto"
                className="px-8 py-4 bg-white text-orange-primary rounded-full hover:scale-105 hover:shadow-2xl transition-all duration-300 font-bold text-lg whitespace-nowrap inline-flex items-center justify-center space-x-2"
              >
                <i className="ri-mail-line"></i>
                <span>Solicitar presupuesto</span>
              </Link>
              <a
                href="https://wa.me/34629501941"
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-4 border-2 border-white text-white rounded-full hover:bg-white hover:text-orange-primary transition-all duration-300 font-bold text-lg whitespace-nowrap inline-flex items-center justify-center space-x-2"
              >
                <i className="ri-whatsapp-line"></i>
                <span>WhatsApp</span>
              </a>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer />
      <WhatsAppButton />
      <ScrollToTopButton />
    </div>
  );
};

// Componente separado para cada sección de servicio
interface ServiceDetailSectionProps {
  service: {
    id: string;
    icon: string;
    title: string;
    description: string;
    includes: string[];
    useCases: string;
    priceFactors: string;
  };
  index: number;
}

const ServiceDetailSection = ({ service, index }: ServiceDetailSectionProps) => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  // Imágenes específicas para cada servicio
  const serviceImages: Record<string, string> = {
    'tabiques': 'https://readdy.ai/api/search-image?query=professional%20worker%20installing%20drywall%20partition%20wall%20panels%20creating%20room%20division%20in%20modern%20interior%20space%20with%20metal%20studs%20and%20clean%20white%20plasterboard%20surfaces%20bright%20natural%20lighting&width=800&height=1000&seq=service-tabiques-001&orientation=portrait',
    'trasdosados': 'https://readdy.ai/api/search-image?query=construction%20worker%20applying%20drywall%20cladding%20to%20exterior%20wall%20with%20insulation%20material%20visible%20metal%20framework%20and%20white%20plasterboard%20panels%20in%20residential%20interior%20bright%20workspace&width=800&height=1000&seq=service-trasdosados-002&orientation=portrait',
    'techos': 'https://readdy.ai/api/search-image?query=professional%20installer%20working%20on%20suspended%20ceiling%20false%20ceiling%20installation%20with%20metal%20grid%20framework%20and%20white%20ceiling%20panels%20in%20modern%20interior%20space%20overhead%20view%20bright%20lighting&width=800&height=1000&seq=service-techos-003&orientation=portrait',
    'aislamiento-acustico': 'https://readdy.ai/api/search-image?query=close%20up%20view%20of%20acoustic%20insulation%20installation%20with%20rock%20wool%20material%20between%20metal%20studs%20soundproofing%20layers%20in%20wall%20construction%20professional%20installation%20bright%20clean%20workspace&width=800&height=1000&seq=service-acustico-004&orientation=portrait',
    'aislamiento-termico': 'https://readdy.ai/api/search-image?query=thermal%20insulation%20material%20installation%20in%20wall%20cavity%20with%20white%20polystyrene%20or%20mineral%20wool%20panels%20energy%20efficiency%20construction%20detail%20professional%20work%20bright%20interior&width=800&height=1000&seq=service-termico-005&orientation=portrait',
    'antihumedad': 'https://readdy.ai/api/search-image?query=green%20moisture%20resistant%20drywall%20panels%20installation%20in%20bathroom%20or%20kitchen%20area%20waterproof%20plasterboard%20construction%20worker%20applying%20special%20hydrophobic%20panels%20bright%20clean%20modern%20space&width=800&height=1000&seq=service-antihumedad-006&orientation=portrait',
  };

  return (
    <section
      ref={ref}
      id={service.id}
      className={`py-20 ${index % 2 === 0 ? 'bg-dark-bg' : 'bg-dark-card'}`}
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
        {/* Content */}
        <motion.div
          initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
          animate={inView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.6 }}
          className={index % 2 === 0 ? 'lg:order-1' : 'lg:order-2'}
        >
          <div className="w-16 h-16 bg-gradient-to-br from-orange-primary to-orange-secondary rounded-xl flex items-center justify-center mb-6">
            <i className={`${service.icon} text-white text-3xl`}></i>
          </div>

          <h2 className="text-4xl font-extrabold text-text-primary mb-4">
            {service.title}
          </h2>
          <p className="text-text-secondary text-lg mb-8 leading-relaxed">
            {service.description}
          </p>

          {/* What's Included */}
          <div className="mb-8">
            <h3 className="text-xl font-bold text-text-primary mb-4 flex items-center">
              <i className="ri-checkbox-circle-line text-orange-primary mr-2"></i>
              Qué incluye
            </h3>
            <ul className="space-y-3">
              {service.includes.map((item, i) => (
                <li key={i} className="flex items-start space-x-3">
                  <i className="ri-arrow-right-s-line text-orange-primary mt-1 flex-shrink-0"></i>
                  <span className="text-text-muted text-[15px]">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Use Cases */}
          <div className="mb-8 p-6 bg-dark-card border border-dark-border rounded-xl">
            <h3 className="text-lg font-bold text-text-primary mb-3 flex items-center">
              <i className="ri-lightbulb-line text-orange-primary mr-2"></i>
              Casos de uso
            </h3>
            <p className="text-text-muted text-[15px] leading-relaxed">
              {service.useCases}
            </p>
          </div>

          {/* Price Factors */}
          <div className="p-6 bg-orange-primary/5 border border-orange-primary/20 rounded-xl">
            <h3 className="text-lg font-bold text-text-primary mb-3 flex items-center">
              <i className="ri-price-tag-3-line text-orange-primary mr-2"></i>
              Qué afecta al precio
            </h3>
            <p className="text-text-muted text-[15px] leading-relaxed">
              {service.priceFactors}
            </p>
          </div>

          {/* CTA */}
          <div className="mt-8">
            <Link
              to="/contacto"
              className="inline-flex items-center space-x-2 px-8 py-4 bg-orange-primary text-white rounded-full hover:shadow-glow-orange transition-all duration-300 font-bold whitespace-nowrap"
            >
              <span>Solicitar presupuesto</span>
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </motion.div>

        {/* Image */}
        <motion.div
          initial={{ opacity: 0, x: index % 2 === 0 ? 30 : -30 }}
          animate={inView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
          className={index % 2 === 0 ? 'lg:order-2' : 'lg:order-1'}
        >
          <div className="relative rounded-2xl overflow-hidden h-[500px]">
            <img
              src={serviceImages[service.id]}
              alt={service.title}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-dark-bg/80 to-transparent"></div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ServiciosPage;
